import {
  Autocomplete,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAsyncAction, useStateRef } from 'app/services';
import { transportGroupUpdate } from 'app/store/transport';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const SettingsForm = forwardRef((props, ref) => {
  const theme = useTheme();

  const { groupItems } = useSelector((state) => state.transport);
  const { asyncAction: updateTransportGroup } = useAsyncAction(transportGroupUpdate);

  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { defaultValues, onSubmit } = props;
  const { control, reset, register, handleSubmit } = useForm({
    defaultValues: {
      pouring_alert_on: defaultValues.pouring_alert_on,
      wpt_ts_json_url: defaultValues ? defaultValues.wpt_ts_json_url : '',
      wpt_ts_json_port: defaultValues ? defaultValues.wpt_ts_json_port : '',
    },
  });

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit(onSubmit)();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        pouring_alert_on: defaultValues.pouring_alert_on,
        wpt_ts_json_url: defaultValues.wpt_ts_json_url,
        wpt_ts_json_port: defaultValues.wpt_ts_json_port,
      });
    }
  }, [defaultValues]);

  const handleGroupChange = (group, value, fieldName) => {
    updateTransportGroup(group.id, {
      ...group,
      [fieldName]: value ? 1 : 0,
    });
  };

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormGroup>
            <FormControlLabel
              label='Включить автоматическое снятия тревог на наливе'
              control={
                <Controller
                  name='pouring_alert_on'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} onChange={field.onChange} {...register(field.name)} />
                  )}></Controller>
              }
            />
            <FormHelperText sx={{ mt: 0 }}>
              Активирует сервис автоматического отключение тревог транспортных средств в зонах типа "Налив".
            </FormHelperText>
          </FormGroup>
        </Grid>
        <Grid item xs={12}>
          <Divider>
            <Typography>ВвЗпК (втягивание в зону по ключу)</Typography>
          </Divider>
          <FormControl variant='outlined' margin='normal' fullWidth>
            <Controller
              name='geoc_transport_groups'
              control={control}
              render={() => (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={groupItems}
                  noOptionsText={<Typography>Нет подходящих вариантов</Typography>}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} placeholder='Название группы транспорта' />}
                  renderOption={(props, option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                      dense
                      sx={{
                        '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                          backgroundColor: 'transparent !important',
                          color: 'inherit !important',
                        },
                        '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: 'transparent !important',
                        },
                        '&.MuiButtonBase-root.MuiMenuItem-root:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        minHeight: 'auto',
                      }}>
                      <ListItemText primary={option.name} />
                      <Checkbox
                        edge='end'
                        checked={Boolean(option.is_send_geoc)}
                        onChange={(event) => handleGroupChange(option, event.target.checked, 'is_send_geoc')}
                        tabIndex={-1}
                        disableRipple
                      />
                    </MenuItem>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '300px',
                      overflow: 'auto',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Divider>
            <Typography>Отправка события приложен ключ</Typography>
          </Divider>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <FormControl variant='outlined' margin='normal' fullWidth>
                <InputLabel>Ссылка</InputLabel>
                <OutlinedInput
                  label='Ссылка'
                  variant='outlined'
                  fullWidth
                  notched={firstUpdate}
                  placeholder='Ссылка'
                  {...register('wpt_ts_json_url', {
                    minLength: 8,
                  })}></OutlinedInput>
                <FormHelperText>Укажите ссылку для отправки события "Приложен ключ"</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl variant='outlined' margin='normal' fullWidth>
                <InputLabel>Порт</InputLabel>
                <OutlinedInput
                  label='Порт'
                  variant='outlined'
                  fullWidth
                  notched={firstUpdate}
                  placeholder='Порт'
                  {...register('wpt_ts_json_port', {
                    pattern: /^([1-9]\d*)$/,
                  })}></OutlinedInput>
                <FormHelperText>Укажите порт ссылки</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          <FormControl variant='outlined' margin='normal' fullWidth>
            <Controller
              name='transport_groups'
              control={control}
              render={() => (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={groupItems}
                  noOptionsText={<Typography>Нет подходящих вариантов</Typography>}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => <TextField {...params} placeholder='Название группы транспорта' />}
                  renderOption={(props, option) => (
                    <MenuItem
                      key={option.id}
                      value={option.id}
                      dense
                      sx={{
                        '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                          backgroundColor: 'transparent !important',
                          color: 'inherit !important',
                        },
                        '&.MuiButtonBase-root.MuiMenuItem-root.Mui-selected:hover': {
                          backgroundColor: 'transparent !important',
                        },
                        '&.MuiButtonBase-root.MuiMenuItem-root:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        minHeight: 'auto',
                      }}>
                      <ListItemText primary={option.name} />
                      <Checkbox
                        edge='end'
                        checked={Boolean(option.is_send_wpt_ts_json)}
                        onChange={(event) => handleGroupChange(option, event.target.checked, 'is_send_wpt_ts_json')}
                        tabIndex={-1}
                        disableRipple
                      />
                    </MenuItem>
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '300px',
                      overflow: 'auto',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
});

export default SettingsForm;
